
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../contexts/AuthContext";
import { AppContext } from "../../../contexts/AppContext";
import { EditWorkflowSidebar } from "./EditWorkflowSidebar";
import CoreDetails from "./CoreDetails";
import { BackLink, Button, CancelButton, PageTitle, PageHeader } from "../../../components/StyledElements";
import { getWorkflowGraph } from "../../../services/workflows"
import Loading from "../../../components/Loading";
import swal from "sweetalert2";
import { WorkflowGraph } from "./WorkflowGraph";
import { DynamicWorkflowGraphTab } from "../../../components/Workflows/DynamicWorkflowGraphTab";
import { WorkflowTriggersTab } from "../../../components/Workflows/WorkflowTriggersTab";

import { useAgents } from "../../../contexts/AgentsContext";
import { resolveGeneratorClientId } from "../../../utils/workflowGenerationRequest";

export const EditWorkflow = () => {
  const { activeUser } = useAuthContext();
  const { selectedClient } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { workflow_id } = useParams();
  const [selectedTab, setSelectedTab] = useState(window.location.hash ? window.location.hash.substr(1) : "details");
  const [showSidebar, setShowSidebar] = useState(true);

  const { selectedWorkflow, setUpdatedWorkflow, updatedWorkflow, canSaveWorkflow, saveUpdatedWorkflow, getDefaultWorkflow } = useAgents();
  const [savingWorkflow, setSavingWorkflow] = useState(false);

  useEffect(() => {
    // A generated workflow can hand its graph over through navigation state, which keeps it
    // viewable even when the saved record cannot be read back from the workflow endpoints.
    const handedOverGraph = location.state?.graph;
    const handedOverName = location.state?.name;

    const applyHandedOverGraph = () => {
      setUpdatedWorkflow({
        public_id: workflow_id ?? "",
        name: handedOverName ?? "",
        description: "",
        purpose: "",
        graph: handedOverGraph,
        client_id: activeUser?.client_id || null,
      });
    };

    // Fetch live event details using workflow_id
    const fetchWorkflow = async () => {
      const results = await getWorkflowGraph(workflow_id, true);
      console.log("!!! WORKFLOW GRAPH RESULTS: ", results);
      if (results.success) {
        const workflow = results.workflow;
        if (workflow.graph && typeof workflow.graph === "string") {
          workflow.graph = JSON.parse(workflow.graph);
        }

        const hasGraph = workflow?.graph && (Array.isArray(workflow.graph.nodes) || Array.isArray(workflow.graph.edges));
        if (!hasGraph && handedOverGraph) {
          setUpdatedWorkflow({ ...workflow, graph: handedOverGraph, name: workflow?.name ?? handedOverName ?? "" });
          return;
        }

        setUpdatedWorkflow(workflow);
      } else if (handedOverGraph) {
        applyHandedOverGraph();
      } else {
        swal.fire({
          title: "Error",
          text: results.message || "Failed to fetch data set details.",
          icon: "error",
        }).then(() => {
          if (activeUser?.user_type.match(/client/)) {
            navigate(`/client/agents/#workflows`);
          }
          else {
            navigate(`/agents/#workflows`);
          }
        });
      }
    };
    if (workflow_id) {
      fetchWorkflow();
    } else {
      setUpdatedWorkflow({
        public_id: "",
        name: "",
        graph: getDefaultWorkflow(),
        description: "",
        purpose: "",
        client_id: activeUser?.client_id || null,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow_id]);

  const saveWorkflow = async () => {
    if (!canSaveWorkflow() || savingWorkflow) {
      return;
    }
    setSavingWorkflow(true);
    const public_id = await saveUpdatedWorkflow();
    if (public_id) {
      // Update url if it's a new event
      window.history.replaceState(null, null, `/workflow/${public_id}`);
      swal.fire({
        title: "Success",
        text: "Workflow saved successfully.",
        icon: "success",
      });
    }
    setSavingWorkflow(false);
  }

  if (!updatedWorkflow && workflow_id) {
    return <Loading>Loading Workflow...</Loading>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "row", backgroundColor: "var(--main-bg)", height: "100%" }}>
      <div style={{ display: "flex", flexDirection: "row", backgroundColor: "var(--main-bg)" }}>
        <EditWorkflowSidebar
          show={showSidebar}
          defaultSelectedTab={selectedTab}
          onNavItemClicked={(navItem) => {
            setSelectedTab(navItem);
          }}
          onToggle={() => {
            setShowSidebar(!showSidebar);
          }}
        />
      </div>
      <div
        style={{
          marginLeft: "20px",
          marginRight: "20px",
          width: `calc(100% - ${showSidebar ? "250px" : "0px"})`,
          minHeight: "calc(100vh - 40px)",
          position: "relative",
          backgroundColor: "var(--content-item-bg)",
          padding: "20px",
          borderRadius: "8px",
          color: "var(--main-font-color)",
          zIndex: 1,
          /*height: "2000px",*/
        }}
      >
        <PageHeader style={{ position: "sticky", top: "0px", backgroundColor: "var(--content-item-bg)", zIndex: 2, display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
          <div>
            <BackLink onClick={() => {
              if (activeUser?.user_type.match(/client/)) {
                navigate(`/client/agents/#workflows`);
              } else {
                navigate(`/agents/#workflows`);
              }
            }} >
              <i className="fa fa-arrow-left" /> Back to Workflows</BackLink>
            <PageTitle title={`${updatedWorkflow?.id ? "Edit" : "New"} Workflow`} style={{ marginBottom: "0px" }} />
            {updatedWorkflow?.title ?
              <PageTitle title={updatedWorkflow?.title} style={{ marginBottom: "10px", fontWeight: "500", fontSize: "20px" }} />
              : null}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", flexDirection: "row", gap: "10px" }}>
            <CancelButton onClick={() => {
              setUpdatedWorkflow(selectedWorkflow);
            }}>Cancel</CancelButton>
            <Button disabled={!canSaveWorkflow() || savingWorkflow} onClick={saveWorkflow} style={{ marginLeft: "auto" }}>{savingWorkflow ? "Saving Changes" : "Save Changes"}</Button>
          </div>
        </PageHeader>
        <div style={{ backgroundColor: "var(--content-item-bg)", marginTop: "20px", marginBottom: "40px", height: "calc(100% - 80px)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", height: "100%" }}>
            <div style={{ width: "100%", height: "100%" }}>
              <CoreDetails
                style={{ display: selectedTab === "details" ? "flex" : "none" }}
                onSelectTab={(tab) => {
                  window.history.replaceState(null, null, `#${tab}`);
                  setSelectedTab(tab);
                }} />
              {selectedTab === "workflow-graph" && (
                <WorkflowGraph style={{ display: "flex" }}
                  onSelectTab={(tab) => {
                    window.history.replaceState(null, null, `#${tab}`);
                    setSelectedTab(tab);
                  }} />
              )}
              {selectedTab === "dynamic-workflow" && (
                <DynamicWorkflowGraphTab
                  graph={updatedWorkflow?.graph}
                  onGraphChange={(graph) => {
                    setUpdatedWorkflow({ ...updatedWorkflow, graph });
                  }}
                  generate={{
                    kind: "graph",
                    defaultName: updatedWorkflow?.name ? `${updatedWorkflow.name} (generated)` : "",
                    defaultDescription: updatedWorkflow?.description ?? "",
                    defaultPurpose: updatedWorkflow?.purpose ?? "",
                    clientId: resolveGeneratorClientId(selectedClient, updatedWorkflow),
                  }}
                  run={{ workflow: updatedWorkflow }}
                />
              )}

              {selectedTab === "triggers" && (
                <WorkflowTriggersTab
                  workflow={updatedWorkflow}
                  clientId={selectedClient?.id ?? updatedWorkflow?.client_id ?? null}
                  onSaved={(saved) => {
                    setUpdatedWorkflow({
                      ...saved,
                      graph:
                        typeof saved.graph === "string"
                          ? JSON.parse(saved.graph)
                          : saved.graph ?? updatedWorkflow?.graph,
                    });
                  }}
                />
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditWorkflow;

